import React, { useState } from 'react';
import trendsRaw from '../data/program_trends.json';
import { fmt } from '../utils';

const CHANGES = {
  2013: {
    headline: 'Program Launch',
    track: 'Track 1 only (one-sided risk)',
    quality: 'Binary quality flag (P4R vs. standard). 220 ACOs in first cohort.',
    financial: 'Benchmark based 100% on ACO historical expenditures. Minimum savings rate (MSR) required before sharing. Up to 50% sharing rate.',
    assignment: 'Retrospective assignment only. Primary care service codes defined.',
    notable: 'No ACO_State field. QualScore was a binary 0/1 flag, not a composite score. Per capita fields lacked BY1/BY2/BY3 suffixes.',
  },
  2014: {
    headline: 'Benchmark Year Weighting Introduced',
    track: 'Track 1 + Track 2 (limited two-sided risk option added)',
    quality: 'QualScore introduced as a numeric composite (0–100). First year with quality data comparable across ACOs.',
    financial: 'BY1/BY2/BY3 benchmark year weighting introduced. Capann fields added with detailed service category breakdowns.',
    assignment: 'ACO_State field added. Expanded primary care service codes.',
    notable: '2014 used lowercase capann_ field names (e.g., capann_hsp vs. CapAnn_HSP) — a known PUF inconsistency.',
  },
  2015: {
    headline: 'Outpatient & Hospice Tracking Added',
    track: 'Track 1 / Track 2',
    quality: 'Quality scoring stable. First year with CapAnn_OPD (outpatient department payments).',
    financial: 'CapAnn_OPD added, completing the major service category breakdown. Track 2 ACOs began accepting limited downside risk.',
    assignment: 'Consistent with 2014. Retrospective assignment standard.',
    notable: 'Most stable schema year. 392 ACOs — largest Track 1 cohort before Pathways restructuring.',
  },
  2016: {
    headline: 'Quality Measures Expanded (ACO1–ACO42)',
    track: 'Track 1 / Track 2 / Track 3 (full two-sided, launched)',
    quality: '42 individual quality measures (ACO1–ACO42) added to the PUF. Agreement type (Initial/Renewal) and Agreement Period Num tracking began.',
    financial: 'DisAdj (disease adjustment) and AIM model flags introduced in 2017, building on 2016 infrastructure.',
    assignment: 'Initial vs. Renewal distinction tracked. Current_Start_Date and Initial_Start_Date fields added.',
    notable: 'Major PUF schema expansion — column count jumped from ~119 to 164. ACO_Num still used as identifier.',
  },
  2017: {
    headline: 'Prior Savings Adjustments & AIM Model',
    track: 'Track 1 / Track 2 / Track 3 (Track 1+ proposed)',
    quality: 'Quality scoring refined. DisAffQual (disease-afflicted quality adjustment) introduced.',
    financial: 'DisAdj, AIM model participation flag, and PriorSavAdj (prior savings adjustment) fields added. Benchmarks became more sophisticated.',
    assignment: 'No major assignment changes. 472 ACOs — peak pre-Pathways participation.',
    notable: 'Final year using ACO_Num as primary identifier. Starting 2018, ACO_ID (A1001 format) replaced the legacy ACO_Num.',
  },
  2018: {
    headline: 'Track 1+ & SNF 3-Day Rule Waiver',
    track: 'Track 1 / Track 1+ (new) / Track 2 / Track 3',
    quality: 'DisAffQual applied selectively. SNF_Waiver, SNF_LOS, SNF_PayperStay fields added.',
    financial: 'Advanced Payment model flags added. Track 1+ offered limited downside risk as a bridge toward Track 3. Peak enrollment: 548 ACOs.',
    assignment: 'First year using ACO_ID. CrossWalk required to link pre-2018 ACO_Num to post-2018 ACO_ID.',
    notable: 'Largest ACO cohort in program history. N_Hosp field introduced. SNF 3-day rule waiver available for Track 2/3 ACOs.',
  },
  2019: {
    headline: 'Pathways to Success Launch (July 2019)',
    track: 'Legacy: Track 1/2/3 (Jan–Dec) · Pathways: Basic A/B/C/D/E + Enhanced (July–Dec)',
    quality: 'Risk_Model field added (One-Sided / Two-Sided). BASIC and ENHANCED track flags introduced.',
    financial: 'Two separate PUF files: PY 2019 (475 ACOs, full year) and PY 2019A (205 Pathways ACOs, 6-month period). PBPM calculations differ — 12 months vs. 6 months.',
    assignment: 'Prospective assignment option introduced. PATHWAYS alignment created new agreement period structures.',
    notable: 'Most complex year in the dataset. 2019A ACOs should not be combined with 2019 in aggregate trend calculations.',
  },
  2020: {
    headline: 'COVID-19 — Extraordinary Utilization Reduction',
    track: 'Pathways: Basic A–E / Enhanced / Legacy tracks phasing out',
    quality: 'DisAffQual = 1 applied to ALL ACOs due to COVID-19 PHE. Quality scoring effectively neutralized for savings eligibility.',
    financial: 'Record $2.3B in earned savings driven by COVID utilization reduction — admissions -15%, ED visits -18% vs. prior year. Benchmarks set pre-COVID created large apparent savings.',
    assignment: 'Assign_Type not yet present in PUF. Some assignment methodology flexibilities applied.',
    notable: 'COVID context is critical for interpreting 2020 data. The savings spike reflects pandemic-driven utilization drops, not ACO-driven efficiency gains alone.',
  },
  2021: {
    headline: 'CAHPS Measures & Sliding-Scale Quality',
    track: 'Pathways: Basic A–E / Enhanced',
    quality: 'CAHPS_1–CAHPS_11 patient experience measures replaced older survey instruments. Sliding-scale quality scoring introduced — ACOs below QPS threshold can still qualify for partial savings based on score.',
    financial: 'Health equity adjustment bonus (up to 10 points) available. Assign_Type (Retrospective / Prospective) added to PUF.',
    assignment: 'Assign_Type field introduced. Prospective assignment fully operational.',
    notable: 'Many numeric fields switched to comma/percent string format in the raw PUF — normalization required. CAHPS data only available 2021+.',
  },
  2022: {
    headline: 'Current_Track Field Replaces Individual Flags',
    track: 'Reported as Current_Track string (no longer individual binary flag columns)',
    quality: 'Health equity adjustment refined. QualScore reverted to plain decimal format. ACO_State dropped from PUF.',
    financial: 'Net savings reached $2.5B. AdV_Pay fields removed from PUF (advanced payment program wound down).',
    assignment: 'No major changes. Continued prospective/retrospective options.',
    notable: 'Current_Track switched to short codes (A through E for BASIC levels, EN for ENHANCED); the explorer maps these to the same track groups used for earlier years.',
  },
  2023: {
    headline: 'Record $3.1B Earned Savings',
    track: 'Pathways tracks (Current_Track field)',
    quality: 'Sliding-scale quality fully embedded. FinalLossRate field added for two-sided risk ACOs. Met_AltQPS introduced.',
    financial: '69% of ACOs generated savings — best rate to date (excluding COVID 2020). FinalLossRate, Impact_Mid_Year_Termination, N_AB_Year_Dual_PY/NonDual_PY fields added.',
    assignment: 'Third consecutive year of stable assignment methodology post-Pathways.',
    notable: 'The 2023 $3.1B in earned savings was a program record at the time, surpassed only by 2024.',
  },
  2024: {
    headline: 'Largest PUF Schema & $4.1B Record Savings',
    track: 'Pathways tracks (Current_Track field)',
    quality: 'Medicare CQM (Clinical Quality Measure) collection type introduced under APP. Health equity adjustment refined (partial-year LIS/dual eligibility, ADI rank exclusions). Met_40pctl replaces Met_30pctl.',
    financial: 'Capped regional HCC risk score growth (symmetrical to ACO cap). Same CMS-HCC model for benchmark/performance years (2024 V28 phase-in: 67% V24 + 33% V28). Eliminated overall negative regional adjustment.',
    assignment: 'Added third step to step-wise methodology (expanded 24-month lookback for NPs/PAs/CNSs). Revised "assignable beneficiary" definition.',
    notable: '189-field schema — largest in program history. Demog_RiskScore and RR_Weight fields added. AIP (Advance Investment Payment) fields introduced. $4.1B in earned savings — all-time program record.',
  },
};

const STAT_COLORS = ['pill-teal','pill-green','pill-blue','pill-amber'];

export default function ProgramChanges() {
  const [expanded, setExpanded] = useState(2024);
  const legacy = trendsRaw.filter(d => d.is_midyear === 0);
  const midyear = trendsRaw.find(d => d.is_midyear === 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Intro */}
      <div className="chart-card">
        <div className="chart-title" style={{ marginBottom: 8 }}>MSSP Program History — 2013 to 2024</div>
        <p style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.6 }}>
          The Medicare Shared Savings Program has undergone significant structural changes across its 12 performance years.
          This page summarizes the key regulatory, quality, financial, and assignment changes for each year,
          alongside the program's actual performance data. Click any year to expand the full detail.
        </p>
        <div style={{ display: 'flex', gap: 20, marginTop: 14, flexWrap: 'wrap' }}>
          {[
            { label: 'Total ACO-Years', value: '5,476' },
            { label: 'Unique ACOs', value: '1,038' },
            { label: 'Total Earned Savings (all years)', value: fmt.dollars(legacy.reduce((s,d) => s + (d.total_earn_save_loss||0), 0)) },
            { label: 'Peak ACO Count', value: '548 (PY 2018)' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)' }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline accordion */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {legacy.map((d, idx) => {
          const yr = d.performance_year;
          const changes = CHANGES[yr] || {};
          const isOpen = expanded === yr;
          const my = yr === 2019 ? midyear : null;

          return (
            <div key={yr} style={{
              background: 'white', borderRadius: 'var(--radius-lg)',
              border: `1.5px solid ${isOpen ? 'var(--teal)' : 'var(--gray-200)'}`,
              overflow: 'hidden', transition: 'border var(--transition)',
            }}>
              {/* Header row */}
              <div onClick={() => setExpanded(isOpen ? null : yr)}
                style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px',
                  cursor: 'pointer', userSelect: 'none',
                  background: isOpen ? 'var(--teal-light)' : 'transparent' }}>
                <div style={{ width: 52, height: 52, borderRadius: 'var(--radius)', flexShrink: 0,
                  background: isOpen ? 'var(--teal)' : 'var(--gray-100)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 14, color: isOpen ? 'white' : 'var(--navy)' }}>
                  {yr}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>
                    {changes.headline || `Performance Year ${yr}`}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    <span className={`timeline-stat-pill ${STAT_COLORS[0]}`}>{fmt.num(d.n_acos)} ACOs</span>
                    <span className={`timeline-stat-pill ${d.total_earn_save_loss > 0 ? 'pill-green' : 'pill-red'}`}>
                      {fmt.dollars(d.total_earn_save_loss)} earned
                    </span>
                    {d.pct_earning_savings != null && (
                      <span className="timeline-stat-pill pill-blue">{d.pct_earning_savings}% earning savings</span>
                    )}
                    {d.avg_sav_rate != null && (
                      <span className={`timeline-stat-pill ${d.avg_sav_rate >= 0 ? 'pill-green' : 'pill-amber'}`}>
                        {(d.avg_sav_rate * 100).toFixed(2)}% avg savings rate
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 18, color: 'var(--gray-400)', flexShrink: 0 }}>
                  {isOpen ? '▲' : '▼'}
                </div>
              </div>

              {/* Expanded content */}
              {isOpen && (
                <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--teal-light)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 16 }}>
                    {[
                      { icon: '🏷', title: 'Track Structure', content: changes.track },
                      { icon: '⭐', title: 'Quality Changes', content: changes.quality },
                      { icon: '💰', title: 'Financial & Benchmark', content: changes.financial },
                      { icon: '👤', title: 'Assignment & Eligibility', content: changes.assignment },
                    ].map(s => s.content && (
                      <div key={s.title} style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius)',
                        padding: '12px 14px', border: '1px solid var(--gray-200)' }}>
                        <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--navy)', marginBottom: 6 }}>
                          {s.icon} {s.title}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.55 }}>{s.content}</div>
                      </div>
                    ))}
                  </div>

                  {changes.notable && (
                    <div className="note-banner" style={{ marginTop: 14 }}>
                      <span>📌</span>
                      <span><strong>Data Note:</strong> {changes.notable}</span>
                    </div>
                  )}

                  {/* Stats grid for this year */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 14 }}>
                    {[
                      { label: 'Total Beneficiaries', value: fmt.shortNum(d.total_n_ab) },
                      { label: 'Avg Expense PBPM',    value: fmt.pbpm(d.avg_expense_pbpm) },
                      { label: 'Avg Composite RAF',   value: d.avg_composite_raf_py?.toFixed(3) ?? '—' },
                      { label: 'SNF Waiver ACOs',     value: fmt.num(d.n_snf_waiver) },
                    ].map(s => (
                      <div key={s.label} style={{ background: 'white', border: '1px solid var(--gray-200)',
                        borderRadius: 'var(--radius)', padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{s.value}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {my && (
                    <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--amber-light)',
                      borderRadius: 'var(--radius)', border: '1px solid #f0c040' }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#7a5800', marginBottom: 4 }}>
                        ⚡ PY 2019A — Pathways to Success Launch Cohort (July–December 2019)
                      </div>
                      <div style={{ fontSize: 13, color: '#7a5800' }}>
                        {fmt.num(my.n_acos)} new Pathways ACOs · {fmt.dollars(my.total_earn_save_loss)} earned ·{' '}
                        {(my.avg_sav_rate * 100).toFixed(1)}% avg savings rate · 6-month performance period only.
                        These ACOs are tracked separately and excluded from year-over-year aggregates.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* CMS resource links */}
      <div className="chart-card">
        <div className="chart-title" style={{ marginBottom: 12 }}>CMS Data Resources</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { label: 'MSSP Performance Year Results PUF', url: 'https://data.cms.gov/medicare-shared-savings-program/performance-year-financial-and-quality-results' },
            { label: 'All MSSP Public Use Files & Historical Data', url: 'https://www.cms.gov/medicare/payment/fee-for-service-providers/shared-savings-program-ssp-acos/data' },
            { label: 'MSSP Statutes, Regulations & Guidance (42 CFR Part 425)', url: 'https://www.cms.gov/medicare/payment/fee-for-service-providers/shared-savings-program-ssp-acos/statutes-regulations' },
            { label: 'MSSP Program Overview & FAQs', url: 'https://www.cms.gov/medicare/payment/fee-for-service-providers/shared-savings-program-ssp-acos' },
          ].map(link => (
            <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                color: 'var(--teal-dark)', padding: '6px 0', borderBottom: '1px solid var(--gray-100)' }}>
              <span style={{ flexShrink: 0 }}>🔗</span>
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
